import * as React from 'react';
import { ChevronDown } from 'lucide-react';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '../../lib/utils';

/**
 * A native `<select>` styled like the other inputs, ported from glp-frontend
 * `components/atoms/select-native-html.tsx`. Prefer it over the Radix `Select`
 * inside forms: it works with `register()`, submits with the form, and gives
 * phones their own picker. The ref is the `<select>` itself.
 */
const nativeSelectVariants = cva(
  'w-full appearance-none truncate rounded-[19px] border border-gray-300 bg-background py-2 pl-3 pr-10 text-sm focus:outline-none focus:border-global-green/40 focus:ring-1 focus:ring-global-green/20 disabled:cursor-not-allowed disabled:opacity-50 aria-[invalid=true]:border-red-500',
  {
    variants: {
      selectSize: {
        default: 'h-10',
        lg: 'h-12 py-3',
      },
    },
    defaultVariants: { selectSize: 'default' },
  },
);

export interface NativeSelectProps
  extends React.SelectHTMLAttributes<HTMLSelectElement>,
    VariantProps<typeof nativeSelectVariants> {
  /** Classes for the wrapper that positions the chevron. */
  wrapperClassName?: string;
}

const NativeSelect = React.forwardRef<HTMLSelectElement, NativeSelectProps>(
  ({ className, wrapperClassName, selectSize, ...props }, ref) => (
    <div className={cn('relative w-full', wrapperClassName)}>
      <select
        ref={ref}
        className={cn(nativeSelectVariants({ selectSize }), className)}
        style={{ backgroundImage: 'none' }}
        {...props}
      />
      <ChevronDown
        aria-hidden='true'
        className='pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 opacity-50'
      />
    </div>
  ),
);
NativeSelect.displayName = 'NativeSelect';

export { NativeSelect, nativeSelectVariants };
